import Button from "../../../../../../Elements/Button/Button";
import { LinkToNewTab } from "../../../../../../Elements/Link/Link";
import { useUser } from "../../../../../../../contexts/userContext/userContext";
import { useContent } from "../../../../../../../contexts/contentContext/contentContext";
import type { UserDataT } from "../../../../../../../models/models";
import { memo } from "react";
import GetTransformedDate from "../../../../../../../utils/dateViewTransformer";
import DaysLeftBar from "../DaysLeftBar";
import useSubscriptionInfo from "../../hooks/useSubscriptionInfo";
import {
  UPGRADE_PERCENTAGE,
  UPGRADE_PERCENTAGE_PRO,
} from "../../helpers/constants";
import Tooltip from "../../../../../../Elements/Tooltip/Tooltip";
import Icon from "../../../../../../Elements/Icon/Icon";

import styles from "./styles.module.scss";

const DAYS_LEFT_WARNING_THRESHOLD = 60;

const Footer = memo(() => {
  const { getTextByKey } = useContent();
  const {
    getUserData,
    isPremium,
    isBusiness,
    isPro,
    isSubscribed,
    getUserBillingDetails,
  } = useUser();
  const userData = getUserData() as UserDataT;
  const {
    daysLeft,
    percentageOfSpace,
    color,
    isOldSubscriberPackage,
    getRenewSubscriptionPath,
    isInGraceSubscriber,
  } = useSubscriptionInfo();

  const expirationDate = GetTransformedDate({
    date: userData.SubscribationExpirationDate,
  });

  /**
   * Returns the path for the upgrade button
   * based on the current subscription status and storage space.
   * @returns The path for the upgrade button.
   */
  const getUpgradeButtonPath = () => {
    // Don't allow to show two buttons at the same time
    if (showRenewButton()) return "";

    const plansPath = "plans";
    const businessPlanPurchasePath = "purchase/business/monthly";

    if (!isSubscribed()) {
      return plansPath;
    } else if (isPro() && !userData?.IsSubUser) {
      // If storage almost full, go to business plan purchase page
      if (+percentageOfSpace > UPGRADE_PERCENTAGE && !isOldSubscriberPackage) {
        return businessPlanPurchasePath;
      } else {
        return plansPath;
      }
    } else {
      return "";
    }
  };

  const showRenewButton = () => {
    return (
      getUserData().IsSubscribed &&
      !getUserData().IsSubUser &&
      daysLeft! < DAYS_LEFT_WARNING_THRESHOLD &&
      !isOldSubscriberPackage &&
      isInGraceSubscriber
    );
  };

  const planName = isSubscribed()
    ? isPremium()
      ? "JM Premium"
      : userData.Subscription
    : getTextByKey("free-user");

  return (
    <div className={styles.footerWrapper}>
      <div className={styles.subscriptionNameSection}>
        <span className={styles.subscriptionName}>{planName}</span>
        {!!getUpgradeButtonPath() ? (
          <Button blank className={styles.upgradePlanButton}>
            {/*@ts-ignore*/}
            <LinkToNewTab page={getUpgradeButtonPath()} isNewTab>
              {getTextByKey("upgrade-subscription-text")}
            </LinkToNewTab>
          </Button>
        ) : null}
        {showRenewButton() && !getUserBillingDetails()?.auto_renew ? (
          !isOldSubscriberPackage ? (
            <Button blank className={styles.upgradePlanButton}>
              {/*@ts-ignore*/}
              <LinkToNewTab page={getRenewSubscriptionPath()} isNewTab>
                {getTextByKey("renew")}
              </LinkToNewTab>
            </Button>
          ) : (
            <Button blank className={styles.upgradePlanButton}>
              {/*@ts-ignore*/}
              <LinkToNewTab page="plans" isNewTab>
                {getTextByKey("upgrade-subscription-text")}
              </LinkToNewTab>
            </Button>
          )
        ) : null}
        {getUserBillingDetails()?.auto_renew ? null : (
          <DaysLeftBar daysLeft={daysLeft!} color={color} />
        )}
      </div>
      {+percentageOfSpace > UPGRADE_PERCENTAGE_PRO &&
      (isBusiness() || (isPremium() && !isOldSubscriberPackage)) ? (
        <div className={styles.noUpgradeMessage}>
          {getTextByKey("user-storage-full")}
        </div>
      ) : null}
      {userData.IsSubUser ? (
        <div className={styles.TopUserName}>
          <span>{getTextByKey("under-organization")} </span>
          <span>#{userData?.Organization || userData.TopUserFullName}</span>
          {/*@ts-ignore*/}
          <Tooltip
            tooltipText={
              (
                <span>
                  {getTextByKey("owned-by")} {userData.TopUserFullName}
                  <br />
                  {userData.TopUserEmail}
                </span>
              ) as unknown as string
            }
          >
            <Icon name="question-circle" />
          </Tooltip>
        </div>
      ) : (
        <div className={styles.expirationDateSection}>
          {isSubscribed() ? (
            getUserBillingDetails()?.auto_renew ? (
              <span>
                {getTextByKey(
                  `reneweble-${
                    getUserBillingDetails()?.period === 1 ? "monthly" : "yearly"
                  }`
                )}
              </span>
            ) : (
              <span>
                {getTextByKey("notice-about-subscription-expiration-date")}{" "}
                {expirationDate}
              </span>
            )
          ) : (
            <span>{getTextByKey("user-isnt-sub-inbox-message")}</span>
          )}
        </div>
      )}
    </div>
  );
});

export default Footer;
